import React, { useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import { FsButton, Heading } from '~/components/core';
import AlertBanner from '~/components/core/AlertBanner';
import { getDownloadErrorMessage, useFileDownload } from '~/components/hooks/useFileDownload';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import Checkbox from '~/components/ui/atoms/Checkbox';
import { reportAxiosError } from '~/Utils';

type DownloadKind = 'json' | 'template';

const ENDPOINT_PATH: Record<DownloadKind, string> = {
  json: 'get_schema/json',
  template: 'get_empty_template',
};

const FALLBACK_FILENAME_SUFFIX: Record<DownloadKind, string> = {
  json: '_import_schemas.zip',
  template: '_import_template.zip',
};

const DataImport: React.FC = () => {
  // @ts-expect-error TODO: useSysconfig hook needs TypeScript definition
  const { organization } = useSysconfig();

  const [shouldGenerateClaimNumbers, setShouldGenerateClaimNumbers] = useState(true);
  const [activeDownload, setActiveDownload] = useState<DownloadKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  // `activeDownload` rather than the hook's `isDownloading`: this screen has two buttons and needs
  // to know *which* one is running to put the spinner on it.
  const { download } = useFileDownload();

  const isBusy = activeDownload !== null;

  const handleDownload = async (kind: DownloadKind) => {
    setActiveDownload(kind);
    setError(null);

    // `download` swallows the request's own failure, but `reportAxiosError` posts to /api/v1/errors
    // and can itself reject on a dead connection -- the finally is what the previous catch-and-report
    // block relied on to re-enable the buttons in that case.
    try {
      await download(
        () =>
          axios.post(
            `/sysconfig/api/v1/organizations/${organization.id}/data_import/${ENDPOINT_PATH[kind]}`,
            {
              additional_configurations: { should_generate_claim_numbers: shouldGenerateClaimNumbers },
            },
            { responseType: 'blob' }
          ),
        `${organization.external_id}${FALLBACK_FILENAME_SUFFIX[kind]}`,
        {
          // This screen shows failures in its own banner, so the hook must not open a dialog on top
          // of it. A failure the server never answered at all (a dropped connection) has nothing to
          // put in the banner beyond the generic copy, so that one is still worth reporting.
          onError: async (err) => {
            if (!axios.isAxiosError(err) || !err.response) {
              await reportAxiosError(err);
            }
            setError(getDownloadErrorMessage(err) ?? 'Failed to download');
          },
        }
      );
    } finally {
      setActiveDownload(null);
    }
  };

  return (
    <CardDialog noCardTitle>
      <Heading className="my-20">Data Import</Heading>

      <div className="mb-20">
        <AlertBanner
          alertType={AlertBanner.ALERT_TYPES.INFO}
          withIcon
          note={
            <>
              Download artifacts for importing data into this organization:
              <ul className="mt-5 mb-0">
                <li>
                  <strong>JSON schema</strong> — per-stage schemas including column types and validation.
                </li>
                <li>
                  <strong>Empty CSV template</strong> — one header-only CSV per stage. Fill in the rows and feed to{' '}
                  <code>/load_to_db</code>.
                </li>
              </ul>
            </>
          }
        />
      </div>

      <div className="mb-20">
        <Checkbox
          label="Generate claim numbers during import"
          checked={shouldGenerateClaimNumbers}
          onChange={(event) => setShouldGenerateClaimNumbers(event.target.checked)}
          disabled={isBusy}
        />
      </div>

      {error && (
        <div className="mb-20">
          <AlertBanner alertType={AlertBanner.ALERT_TYPES.ERROR} withIcon note={error} />
        </div>
      )}

      <div className="mt-20 flex gap-20">
        <FsButton color="primary" variant="contained" onClick={() => handleDownload('template')} disabled={isBusy}>
          {activeDownload === 'template' ? 'Downloading…' : 'Download empty template'}
        </FsButton>
        <FsButton color="primary" variant="outlined" onClick={() => handleDownload('json')} disabled={isBusy}>
          {activeDownload === 'json' ? 'Downloading…' : 'Download JSON schema'}
        </FsButton>
      </div>
    </CardDialog>
  );
};

export default DataImport;
